import React, { useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Image,
  Platform,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "./ThemedText";
import { ThemedPlatformSymbol } from "./ThemedPlatformSymbol";
import { useColorScheme } from "@/hooks/useColorScheme";
import { getEntityRoute } from "./ThemedEntityLink";
import { normalizeEntityType, type EntityType } from "@/constants/entity-icons";
import {
  getChipVisualConfig,
  getTagTypeIcon,
  type ChipEntityType,
} from "@/constants/themes/components/chip-theme";

export { getChipVisualConfig, getTagTypeIcon, type ChipEntityType };

/**
 * Props for the ThemedChip component.
 */
export interface IThemedChipProps {
  /** The entity domain type. Determines the brand color palette and default icon. */
  entityType?: ChipEntityType | string;
  /** Canonical entity ID (used for tap-to-navigate when clickable). */
  id?: string;
  /** Primary label or title displayed in the chip. */
  label?: string;
  /** Secondary label (for shares: recipient name, e.g. "Item Name • Recipient"). */
  secondaryLabel?: string;
  /** Image thumbnail URL (for Media thumbnail or Contact avatar). */
  thumbnailUrl?: string;
  /** Tag type when entityType is "tag" (e.g. "memory", "media", "contact", "group", "system", "general"). */
  tagType?: string;
  /** Whether the current user is the owner of this entity (renders Owner profile symbol). */
  isOwner?: boolean;
  /** Whether this entity represents the logged-on AppUser / self-contact (gold highlight & 'you' symbol). */
  isSelf?: boolean;
  /** Whether this entity has a 5-star rating (renders 5-Star Gold Star symbol). */
  isFiveStar?: boolean;
  /** Custom leading icon name (from Ionicons). Overrides default entity icon. */
  iconName?: keyof typeof Ionicons.glyphMap;
  /** Whether to hide the leading icon. Default is false (icon shown). */
  hideIcon?: boolean;
  /** Callback when chip is pressed. If omitted and `id` is present, navigates to the entity. */
  onPress?: () => void;
  /** Callback when remove (x) button is pressed. When provided, renders the remove button. */
  onRemove?: () => void;
  /** Whether the remove action is enabled/visible (defaults to true if onRemove is provided). */
  canRemove?: boolean;
  /** Whether chip is disabled/read-only (no click navigation). */
  readOnly?: boolean;
  /** Compact density (smaller font and padding). */
  compact?: boolean;
  /** Custom background color override. */
  backgroundColor?: string;
  /** Custom text color override. */
  textColor?: string;
  /** Custom border color override. */
  borderColor?: string;
  /** Container style override. */
  /** If true, the referenced item could not be found and will be marked with a warning and red underline. */
  notFound?: boolean;
  /** Whether the chip is draggable on Web (e.g. inside rich-text editor). */
  draggable?: boolean;
  /** Drag start handler for Web. */
  onDragStart?: (e: any) => void;
  /** Optional children rendered inline after the label (e.g. role toggle segmented control). */
  children?: React.ReactNode;
  /** Container style override. */
  style?: StyleProp<ViewStyle>;
  /** Text style override. */
  textStyle?: StyleProp<TextStyle>;
  /** Accessibility label. */
  accessibilityLabel?: string;
  /** Test identifier for the remove button. */
  removeTestID?: string;
  /** Test identifier. */
  testID?: string;
  /** Optional long press callback. */
  onLongPress?: () => void;
}

/**
 * ThemedChip: Universal compact inline entity chip component (Tier 1 Item Card).
 *
 * Implements the standardized Chip card across Contacts, Groups, Memories, Media,
 * Tags, and Shares. Supports small thumbnails for media, entity-specific brand color
 * palettes, Platform Symbols (Owner, 5-Star), and interactive navigation/removal.
 *
 * @param {IThemedChipProps} props - Component properties.
 * @returns {React.JSX.Element} Rendered themed chip.
 */
export function ThemedChip({
  entityType = "general",
  id,
  label,
  secondaryLabel,
  thumbnailUrl,
  tagType,
  isOwner = false,
  isSelf = false,
  isFiveStar = false,
  iconName,
  hideIcon = false,
  onPress,
  onRemove,
  canRemove = true,
  readOnly = false,
  compact = false,
  notFound = false,
  draggable = false,
  onDragStart,
  onDragEnd,
  children,
  backgroundColor,
  textColor,
  borderColor,
  style,
  textStyle,
  accessibilityLabel,
  testID,
  removeTestID,
  onLongPress,
}: IThemedChipProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);
  const [isRemoveHovered, setIsRemoveHovered] = useState(false);
  const isDark = useColorScheme() === "dark";

  const visualConfig = getChipVisualConfig(entityType, isDark);

  // For the AppUser's own contact chip, apply an archival gold background so it really stands out
  const isSelfAccount = isSelf && (entityType === "contact" || entityType === "user");
  const goldBg = isDark ? "rgba(212, 175, 55, 0.25)" : "rgba(212, 175, 55, 0.18)";
  const goldBorder = isDark ? "rgba(212, 175, 55, 0.60)" : "rgba(212, 175, 55, 0.45)";
  const goldText = isDark ? "#F5DE88" : "#8A6D0B";

  const resolvedBg = backgroundColor ?? (isSelfAccount ? goldBg : visualConfig.bgColor);
  const resolvedBorder = borderColor ?? (isSelfAccount ? goldBorder : visualConfig.borderColor);
  const resolvedText = textColor ?? (isSelfAccount ? goldText : visualConfig.textColor);

  const displayLabel = label || id || `${entityType} item`;

  // Determine if clicking navigates
  const isClickable = !readOnly && (Boolean(onPress) || Boolean(id));

  const handlePress = () => {
    if (readOnly) return;
    if (onPress) {
      onPress();
      return;
    }
    if (id && id !== "unknown") {
      const normType = normalizeEntityType(entityType as EntityType);
      const route = getEntityRoute(normType, id);
      try {
        router.push(route as any);
      } catch (err) {
        console.warn(`[ThemedChip] Navigation failed for route "${route}":`, err);
      }
    }
  };

  const handleRemove = (e: any) => {
    e?.stopPropagation?.();
    if (onRemove) {
      onRemove();
    }
  };

  // Determine leading icon or thumbnail
  const notFoundColor = "#EF4444";
  const resolvedIcon = notFound
    ? ("alert-circle-outline" as const)
    : (iconName ?? (entityType === "tag" ? getTagTypeIcon(tagType) : visualConfig.defaultIcon));
  const iconSize = compact ? 10 : 11;

  const hasRemoveButton = Boolean(onRemove && canRemove && !isSelf);
  const ContainerComponent = isClickable && !hasRemoveButton ? TouchableOpacity : View;

  const chipContent = (
    <>
      {/* 1. Leading Media Thumbnail or Entity Icon */}
      {thumbnailUrl ? (
        <Image
          source={{ uri: thumbnailUrl }}
          style={[
            styles.thumbnail,
            compact ? styles.thumbnailCompact : styles.thumbnailStandard,
          ]}
          resizeMode="cover"
        />
      ) : !hideIcon ? (
        <Ionicons
          name={resolvedIcon}
          size={iconSize}
          color={notFound ? notFoundColor : resolvedText}
          style={styles.leadingIcon}
        />
      ) : null}

      {/* 2. Text Label(s) & Custom Children */}
      <View style={[styles.textContainer, children ? styles.textContainerWithChildren : undefined]}>
        <ThemedText
          numberOfLines={1}
          style={[
            styles.labelText,
            compact ? styles.labelTextCompact : styles.labelTextStandard,
            { color: resolvedText },
            notFound && styles.notFoundText,
            textStyle,
          ]}
        >
          {displayLabel}
        </ThemedText>

        {secondaryLabel ? (
          <ThemedText
            numberOfLines={1}
            style={[
              styles.secondaryLabelText,
              compact ? styles.secondaryLabelTextCompact : styles.secondaryLabelTextStandard,
              { color: resolvedText, opacity: 0.8 },
            ]}
          >
            {` • ${secondaryLabel}`}
          </ThemedText>
        ) : null}

        {children}
      </View>

      {/* 3. Platform Symbols: You gold person, Owner gold key & 5-Star Gold Star */}
      {isSelf ? <ThemedPlatformSymbol type="you" size="small" /> : null}

      {isOwner ? <ThemedPlatformSymbol type="owner" size="small" /> : null}

      {isFiveStar ? <ThemedPlatformSymbol type="fivestar" size="small" /> : null}
    </>
  );

  return (
    <ContainerComponent
      testID={testID}
      accessibilityRole={!hasRemoveButton && isClickable ? "button" : undefined}
      accessibilityLabel={
        !hasRemoveButton
          ? accessibilityLabel || `${entityType} chip: ${displayLabel}${secondaryLabel ? ` • ${secondaryLabel}` : ""}${notFound ? " (Item Not Found)" : ""}`
          : undefined
      }
      onPress={!hasRemoveButton && isClickable ? handlePress : undefined}
      onLongPress={!hasRemoveButton && isClickable ? onLongPress : undefined}
      activeOpacity={isClickable ? 0.75 : 1}
      {...(Platform.OS === "web"
        ? ({
            onMouseEnter: () => isClickable && setIsHovered(true),
            onMouseLeave: () => isClickable && setIsHovered(false),
            draggable: !!draggable,
            onDragStart,
            onDragEnd,
          } as any)
        : {})}
      style={[
        styles.chip,
        compact ? styles.chipCompact : styles.chipStandard,
        Boolean(children) && styles.chipWithChildren,
        {
          backgroundColor: isHovered
            ? isDark
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.04)"
            : resolvedBg,
          borderColor: isHovered ? resolvedText : resolvedBorder,
        },
        notFound && styles.notFoundContainer,
        style,
      ]}
    >
      {hasRemoveButton && isClickable ? (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={
            accessibilityLabel || `${entityType} chip: ${displayLabel}${secondaryLabel ? ` • ${secondaryLabel}` : ""}${notFound ? " (Item Not Found)" : ""}`
          }
          onPress={handlePress}
          onLongPress={onLongPress}
          activeOpacity={0.75}
          style={styles.clickableInner}
        >
          {chipContent}
        </TouchableOpacity>
      ) : (
        chipContent
      )}

      {/* 4. Remove Button (Self contact cannot ever be unlinked/deleted) */}
      {hasRemoveButton ? (
        <TouchableOpacity
          testID={removeTestID}
          accessibilityRole="button"
          accessibilityLabel={`Remove ${displayLabel}`}
          onPress={handleRemove}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          style={[
            styles.removeButton,
            compact ? styles.removeButtonCompact : styles.removeButtonStandard,
            isRemoveHovered && {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.12)",
            },
          ]}
          {...(Platform.OS === "web"
            ? ({
                onMouseEnter: () => setIsRemoveHovered(true),
                onMouseLeave: () => setIsRemoveHovered(false),
              } as any)
            : {})}
        >
          <Ionicons
            name="close"
            size={compact ? 9 : 10}
            color={resolvedText}
          />
        </TouchableOpacity>
      ) : null}
    </ContainerComponent>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 4,
    alignSelf: "flex-start",
    overflow: "hidden",
    ...(Platform.OS === "web"
      ? ({
          userSelect: "none",
          transition: "all 0.15s ease",
          display: "inline-flex",
          verticalAlign: "middle",
        } as any)
      : {}),
  } as any,
  chipStandard: {
    paddingHorizontal: 4.5,
    paddingVertical: 0,
    height: 19,
    maxHeight: 19,
    borderRadius: 4,
    gap: 2.5,
  },
  chipCompact: {
    paddingHorizontal: 3.5,
    paddingVertical: 0,
    height: 17,
    maxHeight: 17,
    borderRadius: 3,
    gap: 2,
  },
  chipWithChildren: {
    height: undefined,
    maxHeight: undefined,
    minHeight: 19,
  },
  leadingIcon: {
    marginRight: 2.5,
  },
  thumbnail: {
    borderRadius: 2,
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
  thumbnailStandard: {
    width: 14,
    height: 14,
  },
  thumbnailCompact: {
    width: 12,
    height: 12,
  },
  textContainer: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: 240,
  },
  textContainerWithChildren: {
    maxWidth: undefined,
  },
  labelText: {
    fontWeight: "600",
    letterSpacing: -0.1,
  },
  labelTextStandard: {
    fontSize: 11,
    lineHeight: 15,
  },
  labelTextCompact: {
    fontSize: 10,
    lineHeight: 13,
  },
  secondaryLabelText: {
    fontWeight: "400",
  },
  secondaryLabelTextStandard: {
    fontSize: 10,
    lineHeight: 14,
  },
  secondaryLabelTextCompact: {
    fontSize: 9,
    lineHeight: 12,
  },
  removeButton: {
    marginLeft: 2,
    borderRadius: 9999,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    ...(Platform.OS === "web"
      ? ({
          cursor: "pointer",
          transition: "background-color 0.15s ease",
        } as any)
      : {}),
  },
  removeButtonStandard: {
    height: 17,
    paddingHorizontal: 2.5,
    paddingVertical: 0,
  },
  removeButtonCompact: {
    height: 15,
    paddingHorizontal: 2,
    paddingVertical: 0,
  },
  clickableInner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2.5,
  },
  notFoundContainer: {
    borderBottomWidth: 2,
    borderBottomColor: "#EF4444",
  },
  notFoundText: {
    textDecorationLine: "underline",
    textDecorationColor: "#EF4444",
    textDecorationStyle: "solid",
  },
});

export default ThemedChip;
